import { array, boolean, object, string } from 'yup';

import { sanitizeAndNormalizeEmail } from '@utils/emailSanitizer';
import { validatePhoneNumber } from '@utils/validation';
import { nameValidation } from '@utils/validationSchemas';

import { getMaxLengthErrMsg, mandatoryErrMessage, validationErrMessages } from '@constants/validationConstants';
import { MAX_50_LENGTH } from '@constants/validationMessages';
import { EUserMultipleOrganisationsFormFields } from './constants';

const getPhoneSchema = (isRequired: boolean) => (
	isRequired
		? string()
			.trim()
			.default('')
			.required(validationErrMessages.phoneNumber)
			.test('validatePhoneNumber', validationErrMessages.contactNumber, validatePhoneNumber)
		: string()
			.trim()
			.default('')
			.test('validatePhoneNumber', validationErrMessages.contactNumberFor2Fa, validatePhoneNumber)
);

export const baseSchema = (
	validateNameFn?: (value: string) => Promise<boolean>,
	checkNameError?: string,
	isPhoneNumberRequired: boolean = true,
) => object({
	[EUserMultipleOrganisationsFormFields.firstName]: nameValidation('First Name'),
	[EUserMultipleOrganisationsFormFields.lastName]: nameValidation('Last Name'),
	[EUserMultipleOrganisationsFormFields.email]: string()
		.default('')
		.transform((value) => sanitizeAndNormalizeEmail(value))
		.lowercase()
		.email(validationErrMessages.validEmail)
		.required(validationErrMessages.email)
		.test(
			'email-unique',
			checkNameError || validationErrMessages.emailUnique,
			async function (value) {
				if (!value) return true;

				const isFormatValid = await string().email().isValid(value);

				if (!isFormatValid) return true;

				try {
					if (!validateNameFn) return true;

					return await validateNameFn(value);
				} catch {
					return true;
				}
			},
		),
	[EUserMultipleOrganisationsFormFields.phoneNumber]: getPhoneSchema(isPhoneNumberRequired),
	[EUserMultipleOrganisationsFormFields.is2FA]: boolean(),
	[EUserMultipleOrganisationsFormFields.organisations]: array()
		.of(
			object()
				.shape({
					organisation: object().nullable().required(mandatoryErrMessage('Minutes')),
					roles: array()
						.nullable()
						.default(null)
						.required(validationErrMessages.roles),
				}),
		).required(),
	[EUserMultipleOrganisationsFormFields.jobTitle]: string()
		.trim()
		.default('')
		.max(MAX_50_LENGTH, getMaxLengthErrMsg('Job Title', MAX_50_LENGTH)),
});
